import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("htss_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const support = localStorage.getItem("operix_support_session");
  if (support) {
    config.headers["X-Support-Session"] = support;
    const supportCompany = localStorage.getItem("operix_support_company");
    if (supportCompany) config.headers["X-Company-Id"] = supportCompany;
    return config;
  }
  const companyId = localStorage.getItem("operix_company_id");
  if (companyId) config.headers["X-Company-Id"] = companyId;
  return config;
});

// FastAPI 422s return detail as an array of objects; pages often toast `detail` directly,
// which crashes React (white screen). Normalise arrays to a readable string once here.
api.interceptors.response.use(undefined, (error) => {
  const d = error?.response?.data;
  if (d && Array.isArray(d.detail)) {
    d.detail_raw = d.detail;
    d.detail = d.detail.map((e) => {
      const field = Array.isArray(e?.loc) ? e.loc.filter((x) => x !== "body").join(".") : "";
      const msg = typeof e?.msg === "string" ? e.msg.replace(/^Value error, /, "") : JSON.stringify(e);
      return field ? `${field}: ${msg}` : msg;
    }).join("; ");
  }
  return Promise.reject(error);
});

export async function downloadReportPdf(reportId, title) {
  const token = localStorage.getItem("htss_token");
  const res = await fetch(`${API}/reports/${reportId}/pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to download PDF");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(title || "report").replace(/\s+/g, "_")}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function downloadQuotePdf(quoteNumber) {
  const token = localStorage.getItem("htss_token");
  const res = await fetch(`${API}/quotes/${quoteNumber}/pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to download PDF");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${quoteNumber}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function downloadTaskInvoicePdf(legacyId) {
  const token = localStorage.getItem("htss_token");
  const res = await fetch(`${API}/tasks/${legacyId}/invoice-pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to download invoice");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Invoice-${legacyId}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function downloadTaskPackingSlip(legacyId) {
  const token = localStorage.getItem("htss_token");
  const res = await fetch(`${API}/tasks/${legacyId}/packing-slip-pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to download packing slip");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `PackingSlip-${legacyId}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function downloadBulkPackingSlips(date) {
  const token = localStorage.getItem("htss_token");
  const qs = date ? `?date=${encodeURIComponent(date)}` : "";
  const res = await fetch(`${API}/web-sales/packing-slips-pdf${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    let msg = "Failed to download packing slips";
    try { msg = (await res.json()).detail || msg; } catch (e) {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `PackingSlips-${date || "latest"}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function downloadBulkInvoices(date) {
  const token = localStorage.getItem("htss_token");
  const qs = date ? `?date=${encodeURIComponent(date)}` : "";
  const res = await fetch(`${API}/web-sales/invoices-pdf${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    let msg = "Failed to download invoices";
    try { msg = (await res.json()).detail || msg; } catch (e) {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Invoices-${date || "latest"}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
